/**
 * Agent-network scenario presets for the Adversarial Robustness game.
 *
 * Each preset is a fixed network of n agents in which every honest pair
 * shares a governed interface (RD-31 §4.1). The action-space sizes (`a`/`A`)
 * feed the quantitative violation-surface bound (RD-31 §4.4). Presets live in
 * a typed module rather than JSON because the layout coordinates and the
 * interface lists are consumed by the board, the rules engine, and the tests.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { GovernedInterface, Scenario } from "../algorithms/types.js";

/** A complete (all-pairs) governed-interface set over the given agent ids. */
function completeInterfaces(ids: readonly string[]): GovernedInterface[] {
  const edges: GovernedInterface[] = [];
  for (let i = 0; i < ids.length; i += 1) {
    for (let j = i + 1; j < ids.length; j += 1) {
      edges.push({ a: ids[i], b: ids[j] });
    }
  }
  return edges;
}

/** Lay n agents out evenly on a circle inside the unit square. */
function ring(
  specs: readonly { id: string; name: string; actionSpace: number }[],
): Scenario["agents"] {
  const n = specs.length;
  return specs.map((spec, index) => {
    const angle = (2 * Math.PI * index) / n - Math.PI / 2;
    return {
      id: spec.id,
      name: spec.name,
      actionSpace: spec.actionSpace,
      x: 0.5 + 0.38 * Math.cos(angle),
      y: 0.5 + 0.38 * Math.sin(angle),
    };
  });
}

const PLANNER_FLEET = ring([
  { id: "A", name: "Planner", actionSpace: 3 },
  { id: "B", name: "Retriever", actionSpace: 2 },
  { id: "C", name: "Coder", actionSpace: 3 },
  { id: "D", name: "Reviewer", actionSpace: 2 },
  { id: "E", name: "Executor", actionSpace: 3 },
]);

const TOOL_MESH = ring([
  { id: "A", name: "Orchestrator", actionSpace: 2 },
  { id: "B", name: "Search Tool", actionSpace: 2 },
  { id: "C", name: "Email Tool", actionSpace: 3 },
  { id: "D", name: "Payments Tool", actionSpace: 3 },
  { id: "E", name: "Filesystem Tool", actionSpace: 2 },
  { id: "F", name: "Browser Tool", actionSpace: 3 },
]);

const VENDOR_GRID = ring([
  { id: "A", name: "Trusted Core", actionSpace: 2 },
  { id: "B", name: "Vendor X Agent", actionSpace: 4 },
  { id: "C", name: "Vendor Y Agent", actionSpace: 4 },
  { id: "D", name: "Audit Agent", actionSpace: 2 },
  { id: "E", name: "Gateway Agent", actionSpace: 3 },
]);

export const SCENARIOS: readonly Scenario[] = [
  {
    id: "planner-fleet",
    title: "Planner Fleet (n = 5)",
    summary:
      "A five-agent reasoning fleet — Planner, Retriever, Coder, Reviewer, Executor — fully connected by governed interfaces. A compact board to feel honest-agent isolation first.",
    agents: PLANNER_FLEET,
    interfaces: completeInterfaces(PLANNER_FLEET.map((agent) => agent.id)),
    forbidLabel: "unauthorized execution (Planner ⇒ Executor without review)",
  },
  {
    id: "tool-mesh",
    title: "Tool Mesh (n = 6)",
    summary:
      "An orchestrator and five tool agents — search, email, payments, filesystem, browser — every pair governed. A trojaned tool is the classic compromised-plugin threat from RD-31 §1.2.",
    agents: TOOL_MESH,
    interfaces: completeInterfaces(TOOL_MESH.map((agent) => agent.id)),
    forbidLabel: "capability laundering (untrusted tool ⇒ payments)",
  },
  {
    id: "vendor-grid",
    title: "Vendor Grid (n = 5)",
    summary:
      "A trusted core flanked by two large-action-space third-party vendor agents, an audit agent, and a gateway. The wide adversary action space stresses the §4.4 surface bound.",
    agents: VENDOR_GRID,
    interfaces: completeInterfaces(VENDOR_GRID.map((agent) => agent.id)),
    forbidLabel: "cross-vendor provenance forgery (Vendor X ⇒ Trusted Core)",
  },
];

export function scenarioById(id: string): Scenario {
  return SCENARIOS.find((scenario) => scenario.id === id) ?? SCENARIOS[0];
}
